import React, { useState, useRef, useCallback } from 'react';
import { ZoomIn, X, Move } from 'lucide-react';

/**
 * ArtworkMagnifier - A magnifying glass component for detailed artwork
 * Shows a zoomed circular lens that follows the cursor
 */
export const ArtworkMagnifier = ({ 
  src, 
  alt, 
  zoomLevel = 3, 
  magnifierSize = 200,
  className = "" 
}) => {
  const [showMagnifier, setShowMagnifier] = useState(false);
  const [magnifierPos, setMagnifierPos] = useState({ x: 0, y: 0 });
  const [cursorPos, setCursorPos] = useState({ x: 0, y: 0 });
  const imgRef = useRef(null);

  const handleMouseMove = useCallback((e) => {
    if (!imgRef.current) return;

    const img = imgRef.current;
    const rect = img.getBoundingClientRect();

    // Cursor position relative to image
    const x = e.clientX - rect.left;
    const y = e.clientY - rect.top;

    // Position for the magnifier (centered on cursor)
    setCursorPos({ x: e.clientX, y: e.clientY });

    // Calculate the position in the zoomed image
    const bgX = (x / rect.width) * 100;
    const bgY = (y / rect.height) * 100;
    setMagnifierPos({ x: bgX, y: bgY });
  }, []);

  return (
    <div className={`relative ${className}`}>
      <img
        ref={imgRef}
        src={src}
        alt={alt}
        className="w-full h-full object-contain cursor-zoom-in"
        onMouseEnter={() => setShowMagnifier(true)}
        onMouseLeave={() => setShowMagnifier(false)}
        onMouseMove={handleMouseMove}
      />
      
      {/* Magnifier Lens */}
      {showMagnifier && (
        <div
          className="fixed pointer-events-none z-50 rounded-full border-4 border-white/80 shadow-2xl overflow-hidden"
          style={{
            width: magnifierSize,
            height: magnifierSize,
            left: cursorPos.x - magnifierSize / 2,
            top: cursorPos.y - magnifierSize / 2,
            backgroundImage: `url(${src})`,
            backgroundSize: `${zoomLevel * 100}%`,
            backgroundPosition: `${magnifierPos.x}% ${magnifierPos.y}%`,
            backgroundRepeat: 'no-repeat',
          }}
        >
          {/* Crosshair in center */}
          <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
            <div className="w-px h-4 bg-white/50" />
            <div className="absolute w-4 h-px bg-white/50" />
          </div>
        </div>
      )}
      
      {/* Zoom indicator */}
      <div className="absolute bottom-2 right-2 bg-black/70 text-white px-2 py-1 rounded text-xs flex items-center gap-1">
        <ZoomIn className="w-3 h-3" />
        Hover to magnify
      </div>
    </div>
  );
};

/**
 * ArtworkDetailViewer - Full-screen pan and zoom viewer for detailed artwork
 */
export const ArtworkDetailViewer = ({ 
  src, 
  alt, 
  isOpen, 
  onClose,
  title = "Artwork Detail View"
}) => {
  const [scale, setScale] = useState(1);
  const [position, setPosition] = useState({ x: 0, y: 0 });
  const [isDragging, setIsDragging] = useState(false);
  const [dragStart, setDragStart] = useState({ x: 0, y: 0 });
  const containerRef = useRef(null);

  const handleWheel = useCallback((e) => {
    e.preventDefault();
    const delta = e.deltaY > 0 ? -0.2 : 0.2;
    setScale(prev => Math.min(Math.max(prev + delta, 1), 8));
  }, []);

  const handleMouseDown = (e) => {
    if (scale > 1) {
      setIsDragging(true);
      setDragStart({ x: e.clientX - position.x, y: e.clientY - position.y });
    }
  };

  const handleMouseMove = (e) => {
    if (isDragging) {
      setPosition({
        x: e.clientX - dragStart.x,
        y: e.clientY - dragStart.y
      });
    }
  };

  const handleMouseUp = () => {
    setIsDragging(false);
  };

  const resetView = () => {
    setScale(1);
    setPosition({ x: 0, y: 0 });
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-[100] bg-black flex flex-col">
      {/* Header */}
      <div className="flex items-center justify-between p-4 bg-black/80 border-b border-white/10">
        <div>
          <h2 className="text-white font-bold">{title}</h2>
          <p className="text-white/60 text-sm">Scroll to zoom • Drag to pan • Click buttons to adjust</p>
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={() => setScale(prev => Math.min(prev + 0.5, 8))}
            className="px-3 py-2 bg-white/10 hover:bg-white/20 text-white rounded-lg flex items-center gap-1"
          >
            <ZoomIn className="w-4 h-4" /> Zoom In
          </button>
          <button
            onClick={() => setScale(prev => Math.max(prev - 0.5, 1))}
            className="px-3 py-2 bg-white/10 hover:bg-white/20 text-white rounded-lg"
          >
            Zoom Out
          </button>
          <button
            onClick={resetView}
            className="px-3 py-2 bg-white/10 hover:bg-white/20 text-white rounded-lg"
          >
            Reset
          </button>
          <div className="px-3 py-2 bg-primary/20 text-primary rounded-lg font-mono">
            {Math.round(scale * 100)}%
          </div>
          <button
            onClick={onClose}
            className="p-2 bg-red-500/20 hover:bg-red-500/30 text-red-400 rounded-lg"
          >
            <X className="w-5 h-5" />
          </button>
        </div>
      </div>

      {/* Image Container */}
      <div
        ref={containerRef}
        className="flex-1 overflow-hidden cursor-grab active:cursor-grabbing"
        onWheel={handleWheel}
        onMouseDown={handleMouseDown}
        onMouseMove={handleMouseMove}
        onMouseUp={handleMouseUp}
        onMouseLeave={handleMouseUp}
      >
        <div
          className="w-full h-full flex items-center justify-center"
          style={{
            transform: `translate(${position.x}px, ${position.y}px)`,
            transition: isDragging ? 'none' : 'transform 0.1s ease-out'
          }}
        >
          <img
            src={src}
            alt={alt}
            className="max-w-none"
            style={{
              transform: `scale(${scale})`,
              transition: 'transform 0.2s ease-out',
              imageRendering: scale > 2 ? 'pixelated' : 'auto'
            }}
            draggable={false}
          />
        </div>
      </div>

      {/* Zoom Presets */}
      <div className="absolute bottom-4 left-1/2 -translate-x-1/2 flex gap-2 bg-black/80 p-2 rounded-xl">
        {[1, 2, 3, 4, 6, 8].map(z => (
          <button
            key={z}
            onClick={() => { setScale(z); setPosition({ x: 0, y: 0 }); }}
            className={`px-3 py-1 rounded-lg text-sm font-medium transition-colors ${
              Math.round(scale) === z 
                ? 'bg-primary text-black' 
                : 'bg-white/10 text-white hover:bg-white/20'
            }`}
          >
            {z}x
          </button>
        ))}
      </div>

      {/* Instructions */}
      <div className="absolute bottom-4 right-4 bg-black/80 text-white/60 text-xs p-3 rounded-lg max-w-xs">
        <div className="flex items-center gap-2 mb-1">
          <Move className="w-4 h-4" />
          <span>Drag to pan when zoomed</span>
        </div>
        <div className="flex items-center gap-2">
          <ZoomIn className="w-4 h-4" />
          <span>Scroll or use buttons to zoom</span>
        </div>
      </div>
    </div>
  );
};

export default ArtworkMagnifier;
